import React, { useEffect, useState } from 'react';

// Reference Numbers brief: the number itself is the same permanent one for
// every account (customer_number) -- only the visible LABEL changes by
// role, matching "Vehicle provider #205814" / "Driver #304910" etc.
export function referenceLabelFor(role) {
  if (['vehicle_owner', 'fleet_manager', 'business_admin'].includes(role)) return 'Vehicle Provider';
  if (['hotel_host', 'event_host'].includes(role)) return 'Accommodation Provider';
  if (['driver', 'bodaboda'].includes(role)) return 'Driver';
  return 'Customer';
}

const MAX_LISTED = 8;

// Account deletion confirmation (Kayd's 16.10.2026 request). One account:
// the admin types that account's six-digit customer number -- digits only,
// no "Customer", no "#", no name. Several accounts: the admin types how
// many accounts are being deleted. The server checks the same value again
// (DELETE /api/admin/users), so this dialog is only the first gate.
export default function ConfirmAccountDeletionModal({ accounts, pending, serverError, onCancel, onConfirm }) {
  const [value, setValue] = useState('');
  const single = accounts.length === 1;
  const expected = single ? String(accounts[0].customer_number ?? '') : String(accounts.length);
  const typed = value.trim();
  const matches = expected !== '' && typed === expected;
  const showMismatch = typed !== '' && !matches && (typed.length >= expected.length || !expected.startsWith(typed));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !pending) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, pending]);

  const submit = (e) => {
    e.preventDefault();
    if (matches && !pending) onConfirm(typed);
  };

  const ref = (a) => `${referenceLabelFor(a.user_role)} #${a.customer_number}`;

  return (
    <div style={s.overlay} onClick={() => !pending && onCancel()}>
      <form
        style={s.box}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-deletion-title"
      >
        <h3 id="confirm-deletion-title" style={s.title}>
          {single ? 'Confirm Account Deletion' : 'Confirm Bulk Deletion'}
        </h3>

        {single ? (
          <p style={s.text}>
            You are about to permanently delete the account of{' '}
            <strong className="notranslate" translate="no">{ref(accounts[0])}</strong>.
          </p>
        ) : (
          <>
            <p style={s.text}>
              You are about to permanently delete <strong>{accounts.length} accounts</strong>:
            </p>
            <ul style={s.list} className="notranslate" translate="no">
              {accounts.slice(0, MAX_LISTED).map((a) => <li key={a.id}>{ref(a)}</li>)}
              {accounts.length > MAX_LISTED && <li>… and {accounts.length - MAX_LISTED} more</li>}
            </ul>
          </>
        )}
        <p style={{ ...s.text, fontWeight: 700 }}>This action cannot be undone.</p>

        <label htmlFor="confirm-deletion-input" style={s.label}>
          {single ? 'Enter the six-digit customer number to confirm:' : `Enter the number of accounts (${accounts.length}) to confirm:`}
        </label>
        <input
          id="confirm-deletion-input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={single ? 6 : 4}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ ...s.input, borderColor: showMismatch ? '#dc2626' : '#d1d5db' }}
          aria-invalid={showMismatch}
          aria-describedby="confirm-deletion-error"
          autoFocus
          disabled={pending}
        />
        <p id="confirm-deletion-error" style={s.error} role="alert">
          {showMismatch ? (single ? 'The customer number does not match.' : 'The number of accounts does not match.') : (serverError || '')}
        </p>

        <div style={s.actions}>
          <button type="button" style={s.cancel} onClick={onCancel} disabled={pending}>Cancel</button>
          <button
            type="submit"
            style={{ ...s.confirm, ...(matches && !pending ? {} : s.confirmDisabled) }}
            disabled={!matches || pending}
          >
            {pending ? 'Deleting…' : single ? 'Permanently Delete Account' : `Permanently Delete ${accounts.length} Accounts`}
          </button>
        </div>
      </form>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16, boxSizing: 'border-box',
  },
  box: {
    background: '#fff', borderRadius: 12, padding: '24px 24px 20px', width: '100%', maxWidth: 460,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)', boxSizing: 'border-box', maxHeight: '100%', overflowY: 'auto',
  },
  title: { marginTop: 0, marginBottom: 12, fontSize: 18, fontWeight: 700, color: '#111827' },
  text: { fontSize: 14, color: '#374151', margin: '0 0 10px', lineHeight: 1.5 },
  list: { fontSize: 14, color: '#374151', margin: '0 0 10px', paddingLeft: 20, lineHeight: 1.6 },
  label: { display: 'block', fontSize: 14, color: '#374151', fontWeight: 600, margin: '6px 0 8px' },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1.5px solid #d1d5db',
    borderRadius: 8, fontSize: 16, letterSpacing: '0.08em', fontVariantNumeric: 'tabular-nums',
  },
  error: { minHeight: 20, margin: '6px 0 0', fontSize: 13, color: '#b91c1c' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end', marginTop: 12 },
  cancel: {
    padding: '9px 18px', background: '#f3f4f6', border: '1px solid #e2e8f0', borderRadius: 8,
    cursor: 'pointer', fontSize: 14, fontWeight: 500,
  },
  confirm: {
    padding: '9px 18px', background: '#dc2626', border: 'none', color: '#fff', borderRadius: 8,
    cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
  confirmDisabled: { background: '#fca5a5', cursor: 'not-allowed' },
};
